import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://pp-studio-website.web.app";
const DEFAULT_OG = `${SITE_URL}/og-default.jpg`;
const AUTHOR = "Pooja Biswal";
const SITE_NAME = "The PP Studio";

const absoluteUrl = (value, fallback = DEFAULT_OG) => {
  if (!value) return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/')) return `${SITE_URL}${value}`;
  return `${SITE_URL}/${value}`;
};

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG,
  ogType = "website",
  noIndex = false,
  schema = null,
  breadcrumbs = null,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — ${AUTHOR}`;
  const url = canonical ? `${SITE_URL}${canonical}` : null;
  const imageUrl = absoluteUrl(ogImage);

  const breadcrumbSchema = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: `${SITE_URL}${item.path}`,
        })),
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={AUTHOR} />
      <meta name="language" content="en-GB" />
      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      )}
      {url && <link rel="canonical" href={url} />}
      <link rel="alternate" type="application/rss+xml" title="The PP Podcast RSS" href={`${SITE_URL}/api/podcast/rss`} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />
      {url && <meta property="og:url" content={url} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />
      <meta name="twitter:creator" content="@ppstudio" />

      {/* JSON-LD schemas */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}

/* ── Pre-built schema factories ─────────────────────────── */

export const SITE_URL_BASE = SITE_URL;

export function personSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: AUTHOR,
    jobTitle: "Contract Specialist",
    url: SITE_URL,
    image: DEFAULT_OG,
    description:
      "LLM (Distinction), Queen Mary University of London. Commercial contract specialist helping freelancers and businesses draft, review and negotiate contracts.",
    alumniOf: {
      "@type": "CollegeOrUniversity",
      name: "Queen Mary University of London",
    },
    knowsAbout: [
      "Contract Law",
      "Commercial Contracts",
      "Non-Disclosure Agreements",
      "Freelance Contracts",
      "Intellectual Property",
      "Service Agreements",
      "Contract Negotiation",
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "London",
      addressCountry: "GB",
    },
    sameAs: [],
  };
}

export function professionalServiceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#professional-service`,
    name: SITE_NAME,
    url: SITE_URL,
    description:
      "Professional contract specialist services — review, drafting, negotiation and commercial legal advice for freelancers and SMEs across the UK.",
    provider: { "@type": "Person", name: AUTHOR },
    areaServed: { "@type": "Country", name: "United Kingdom" },
    serviceType: [
      "Contract Review",
      "Contract Drafting",
      "Contract Negotiation",
      "NDA Drafting",
      "Service Agreement Review",
      "Commercial Legal Advice",
    ],
    priceRange: "££",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Contract support services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Contract review" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Contract drafting" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "NDA drafting" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Service agreement review" } },
      ],
    },
  };
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description:
      "Contract specialist, legal insights, and professional growth — by Pooja Biswal, LLM.",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/contract-tips?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function blogPostingSchema({ title, description, slug, image, datePublished, dateModified, keywords }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    inLanguage: "en-GB",
    url: `${SITE_URL}/blog/${slug}`,
    image: absoluteUrl(image),
    datePublished,
    dateModified: dateModified || datePublished,
    keywords,
    author: {
      "@type": "Person",
      name: AUTHOR,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/pp-favicon-192.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
  };
}

export function articleSchema({ title, description, slug, image, datePublished, dateModified, keywords }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    inLanguage: "en-GB",
    url: `${SITE_URL}/contract-tips/${slug}`,
    image: absoluteUrl(image),
    datePublished,
    dateModified: dateModified || datePublished,
    keywords,
    articleSection: "Contract Law",
    author: {
      "@type": "Person",
      name: AUTHOR,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/pp-favicon-192.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/contract-tips/${slug}` },
  };
}

export function podcastSeriesSchema(episodes = []) {
  return {
    "@context": "https://schema.org",
    "@type": "PodcastSeries",
    name: "The PP Podcast",
    description:
      "Conversations about contracts, freelance business, commercial law and professional growth — hosted by Pooja Biswal.",
    url: `${SITE_URL}/podcast`,
    inLanguage: "en-GB",
    author: { "@type": "Person", name: AUTHOR },
    image: DEFAULT_OG,
    numberOfEpisodes: episodes.length || undefined,
  };
}
